import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC, trpcClient, type RouterOutputs } from "@/trpc";
import { suggestionSourcesForAnchor } from "@/components/ai-suggestion/suggestion-source-priority";
import posthog from "posthog-js";
import { Loader2, AlertCircle } from "lucide-react";
import { DocusealFormEmbed } from "./docuseal-form-embed";
import { PdfPrefillNotice, type PdfFillSuggestion } from "./pdf-prefill-notice";
import { PdfResumeNotice } from "./pdf-resume-notice";
import type { FormAnchorRef } from "../form-anchor-ref";

type FinalizeSigningResult =
  RouterOutputs["organizationForm"]["finalizePdfSigning"];

interface AcceptedPrefillValue {
  formFieldId: string;
  value: string;
}

interface PdfSignEmbedProps {
  formId: string;
  /** The assignment or hiring-process item this signature fulfills. */
  anchor: FormAnchorRef;
  /** Called once the signing session finalizes — inspect `signerStatus` to
   * know whether the other party (or this one) still needs to sign. */
  onSigned?: (result: FinalizeSigningResult) => void;
  /** Called when finalizing an already-submitted signature fails. Fetching
   * the embed itself renders its own inline error and doesn't call this. */
  onError?: (message: string) => void;
  /** Reports whether the embed is loading or finalizing, so a host can
   * disable its own controls (e.g. a Cancel button) meanwhile. */
  onBusyChange?: (busy: boolean) => void;
}

/**
 * Org-side (or hiring-process) signing surface for a multi-party file form:
 * fetches the submitter's `<docuseal-form>` embed for the given anchor, then
 * finalizes on completion (which records the signature and, if every party
 * already signed, stores the fully-signed PDF). Polls briefly if the vendor
 * is still flattening the document right after the last signature.
 *
 * Before opening the embed, checks whether the candidate has past-submission
 * values that fit this form's prefillable fields (server-gated — org actors
 * always get none) and, if so, applies them all automatically before the
 * session is created (or updated, if the org already started it). A sticky
 * disclosure note renders above the embed telling the signer which fields
 * were auto-filled; any prefill failure falls through to the plain embed
 * with no note. Reopening a session the signer already started shows a
 * neutral resume notice instead — the AI disclosure renders only on the open
 * that applied the prefill.
 */
export function PdfSignEmbed({
  formId,
  anchor,
  onSigned,
  onError,
  onBusyChange,
}: PdfSignEmbedProps) {
  const trpc = useTRPC();

  const assignmentId =
    anchor.kind === "assignment" ? anchor.assignmentId : undefined;
  const jobPostId = anchor.kind === "hiring" ? anchor.jobPostId : undefined;
  const hiringProcessItemId =
    anchor.kind === "hiring" ? anchor.hiringProcessItemId : undefined;
  const candidateUserId =
    anchor.kind === "hiring" ? anchor.candidateUserId : undefined;

  type EmbedResult = {
    formSubmissionId: string;
    embed: { kind: "docuseal-form"; src: string; scriptUrl: string };
  };
  const [embedResult, setEmbedResult] = useState<EmbedResult | null>(null);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);

  // Prefill runs between mount and the embed fetch when the server finds
  // candidate values for this session. `appliedSuggestions` is set only once
  // prefill demonstrably succeeded — null means no disclosure note, because
  // a note over an unprefilled form would lie.
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);
  const [appliedSuggestions, setAppliedSuggestions] = useState<
    PdfFillSuggestion[] | null
  >(null);
  // Mutually exclusive with a fresh prefill by construction: a resumed
  // session means this signer opened their embed before, which is exactly
  // what closes the prefill window server-side.
  const [isResumedSession, setIsResumedSession] = useState(false);
  const suggestionsFetchedRef = useRef(false);

  // The session-scoping input shared by the suggestions query and both
  // embed/prefill write paths — an assignment carries only `assignmentId`,
  // never the hiring fields (the server rejects a request combining both).
  const buildAnchorInput = () =>
    assignmentId
      ? { formId, assignmentId }
      : {
          formId,
          hiringProcessJobPostId: jobPostId,
          hiringProcessItemId,
          candidateUserId,
        };

  const getEmbed = useMutation(
    trpc.organizationForm.getPdfSigningEmbed.mutationOptions(),
  );
  const mutateRef = useRef(getEmbed.mutate);
  mutateRef.current = getEmbed.mutate;

  // Fetch (or resume) the submitter's embed, optionally seeding it with
  // prefill values — only applied by the server when this call is the one
  // that creates the session (ignored on resume).
  const startEmbed = (prefill?: PdfFillSuggestion[]) => {
    const anchorInput = buildAnchorInput();
    const prefillValues: AcceptedPrefillValue[] | undefined = prefill?.map(
      (suggestion) => ({
        formFieldId: suggestion.formFieldId,
        value: suggestion.value,
      }),
    );
    mutateRef.current(
      prefillValues && prefillValues.length > 0
        ? { ...anchorInput, prefillValues }
        : anchorInput,
      {
        onSuccess: (data) => {
          setEmbedResult(data as EmbedResult);
          if (prefill && prefill.length > 0) {
            setAppliedSuggestions(prefill);
            posthog.capture("pdf_prefill.shown", {
              formId,
              suggestedFieldCount: prefill.length,
            });
          }
        },
        onError: (err) => {
          if (prefill && prefill.length > 0) {
            // Prefill must never block signing: the server may reject the
            // prefill payload (e.g. the form's prefillable set changed
            // between the suggestions fetch and this call), so retry once
            // without it. No note on the retry — nothing was filled.
            startEmbedRef.current();
            return;
          }
          setEmbedError(err.message || "Failed to open the signing form");
        },
      },
    );
  };
  const startEmbedRef = useRef(startEmbed);
  startEmbedRef.current = startEmbed;

  // Check once per mount whether the candidate has prefillable suggestions
  // for this session. A failed check (or no suggestions, or an org actor —
  // always server-gated to none) must never block signing, so it falls
  // through to the exact same embed fetch as before this feature existed.
  useEffect(() => {
    if (suggestionsFetchedRef.current) {
      return;
    }
    suggestionsFetchedRef.current = true;

    const anchorInput = buildAnchorInput();

    trpcClient.organizationForm.getPdfFillSuggestions
      .query({ ...anchorInput, sources: suggestionSourcesForAnchor(anchor) })
      .then((result) => {
        setIsResumedSession(result.isResumedSession);
        if (!result.canPrefill || result.suggestions.length === 0) {
          setIsLoadingSuggestions(false);
          startEmbedRef.current();
          return;
        }
        const suggestions = result.suggestions;

        if (result.hasExistingSession) {
          // The org already created this session (assignments are always
          // org-first) — inject the values into it before the embed's first
          // open stamps the signer and permanently closes the prefill
          // window. Best-effort: if applying fails, open the embed
          // unprefilled with no note rather than block signing on it.
          trpcClient.organizationForm.applyPdfPrefill
            .mutate({
              ...anchorInput,
              prefillValues: suggestions.map((suggestion) => ({
                formFieldId: suggestion.formFieldId,
                value: suggestion.value,
              })),
            })
            .then(() => {
              setAppliedSuggestions(suggestions);
              posthog.capture("pdf_prefill.shown", {
                formId,
                suggestedFieldCount: suggestions.length,
              });
            })
            .catch(() => {
              // Best-effort — the embed opens unprefilled below.
            })
            .finally(() => {
              setIsLoadingSuggestions(false);
              startEmbedRef.current();
            });
          return;
        }

        // No session yet: the embed-creating call itself carries the
        // prefill; the note renders (and pdf_prefill.shown fires) from its
        // onSuccess.
        setIsLoadingSuggestions(false);
        startEmbedRef.current(suggestions);
      })
      .catch(() => {
        setIsLoadingSuggestions(false);
        startEmbedRef.current();
      });
  }, [
    formId,
    jobPostId,
    hiringProcessItemId,
    candidateUserId,
    assignmentId,
    anchor,
  ]);

  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  const isLoadingEmbed = getEmbed.isPending && !embedResult;
  const isBusy = isLoadingSuggestions || isLoadingEmbed || isFinalizing;
  useEffect(() => {
    onBusyChangeRef.current?.(isBusy);
  }, [isBusy]);

  const finalize = async () => {
    if (!embedResult || isFinalizing) {
      return;
    }
    setIsFinalizing(true);
    try {
      // The vendor may still be flattening the final PDF in the moment right
      // after the last signature; the server reports `processing` in that
      // case. Poll briefly until it resolves — when `processing` is false,
      // pending is final for this call (we're still waiting on another
      // party) and we stop immediately.
      let res = await trpcClient.organizationForm.finalizePdfSigning.mutate({
        formSubmissionId: embedResult.formSubmissionId,
      });
      for (let i = 0; i < 5 && res.processing; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        res = await trpcClient.organizationForm.finalizePdfSigning.mutate({
          formSubmissionId: embedResult.formSubmissionId,
        });
      }
      onSigned?.(res);
    } catch (err) {
      onError?.(
        err instanceof Error ? err.message : "Failed to record signature",
      );
    } finally {
      setIsFinalizing(false);
    }
  };

  if (isLoadingSuggestions || isLoadingEmbed) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 gap-3">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
        <p className="text-sm text-gray-500">Loading signing form...</p>
      </div>
    );
  }
  if (embedError) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 gap-3 px-6">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="text-sm text-red-600 text-center">{embedError}</p>
      </div>
    );
  }
  if (isFinalizing) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 gap-3">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
        <p className="text-sm text-gray-500">Finalizing...</p>
      </div>
    );
  }
  if (embedResult) {
    return (
      <>
        {appliedSuggestions && appliedSuggestions.length > 0 && (
          <PdfPrefillNotice
            suggestions={appliedSuggestions}
            onViewDetails={() =>
              posthog.capture("pdf_prefill.details_viewed", {
                formId,
                suggestedFieldCount: appliedSuggestions.length,
              })
            }
          />
        )}
        {!appliedSuggestions && isResumedSession && <PdfResumeNotice />}
        <DocusealFormEmbed
          src={embedResult.embed.src}
          scriptUrl={embedResult.embed.scriptUrl}
          onCompleted={() => void finalize()}
        />
      </>
    );
  }
  return null;
}
